const Helpers = {
    /**
     * Asynchronously call the given function.
     *
     * Handy for offloading work from of the current call stack.
     *
     * @param callback
     */
    async(callback) {
        if (typeof callback === 'function') {
            return setTimeout(callback, 0);
        } else {
            let time = 0;

            if (typeof callback === 'number') {
                time = callback;
            }

            return new Promise(resolve => {
                setTimeout(resolve, time);
            });
        }
    },

    /**
     * Creates a wrapper function that throttles the number of times 'callback' can be called.
     *
     * @param {number} interval - The time to wait between calls of 'callback'
     * @param {function} callback - The callback to be called
     * @param {object} [self] - An object to set as the 'this' variable for the callback, so bind does not have to be used.
     *
     * @returns {Function} - A wrapper function that will throttle the number of times 'callback' can be called consecutively
     */
    throttledFunction(interval, callback, self) {
        if (typeof interval !== 'number' || isNaN(interval)) {
            throw new TypeError('interval should be a valid Number > 0');
        }

        if (typeof callback !== 'function') {
            throw new TypeError('callback should be a valid callable value');
        }

        if (typeof self === 'undefined') {
            self = null;
        }

        let timer = null,
            lastArguments;

        function timerFunction() {
            timer = null;

            callback.apply(self, lastArguments);
        }

        return () => {
            lastArguments = arguments;

            if (timer !== null) {
                return;
            }

            timer = setTimeout(timerFunction, interval);
        }
    },

    /**
     * Creates a wrapper function that will execute 'callback', 'delay' milliseconds after calling said wrapper.
     *
     * @param {number} delay - The time to wait before calling the callback
     * @param {function} callback - The callback to be called
     * @param {object} [self] - An object to set as the 'this' variable for the callback, so bind does not have to be used.
     *
     * @returns {Function} - A wrapper function that will execute 'callback', 'delay' milliseconds after calling this wrapper.
     */
    delayedFunction(delay, callback, self) {
        if (typeof delay !== 'number' || isNaN(delay)) {
            throw new TypeError('delay should be a valid Number > 0');
        }

        if (typeof callback !== 'function') {
            throw new TypeError('callback should be a valid callable value');
        }

        if (typeof self === 'undefined') {
            self = null;
        }

        let timer = null,
            lastArguments;

        function timerFunction() {
            timer = null;

            callback.apply(self, lastArguments);
        }

        return function () {
            lastArguments = arguments;

            if (timer !== null) {
                clearTimeout(timer);
            }

            timer = setTimeout(timerFunction, delay);
        };
    },
    
    repeatedFunction(count, stagger, callback, self = null) {
        let timeout,
            currentCount,
            lastArgs;

        let setTimer = () => {
            timeout = setTimeout(timerFunction, stagger);
        };
        
        let timerFunction = () => {
            currentCount++;
            
            if (currentCount < count) {
                setTimer();
            }
            
            callback.apply(self, lastArgs);
        };
        
        return (...args) => {
            lastArgs = args;
            
            if (timeout) {
                clearTimeout(timeout);
            }
            
            currentCount = 0;
            
            timerFunction();
        }
    },

    getScrollTop() {
        return window.pageYOffset || document.documentElement.scrollTop;
    },

    getScrollLeft() {
        return window.pageXOffset || document.documentElement.scrollLeft;
    },

    getScroll() {
        return {
            top: Helpers.getScrollTop(),
            left: Helpers.getScrollLeft()
        }
    },

    /**
     * Get rect and add width and height properties if missing.
     *
     * @param element
     * @returns {Object|ClientRect|*}
     */
    getRect(element) {
        let rect = element.getBoundingClientRect();

        if (typeof rect.width === 'number') {
            return {
                width: rect.right - rect.left,
                height: rect.bottom - rect.top,
                top: rect.top,
                left: rect.left,
                right: rect.right,
                bottom: rect.bottom,
                x: rect.left,
                y: rect.top
            };
        }

        return rect;
    },

    /**
     * Repeat the given function a number of times.
     * 
     * @param callback
     * @param interval
     * @param count
     */
    repeat(callback, interval, count) {
        let current = 0,
            max = count || 0;
        
        function advance() {
            if (current < max) {
                ++current;
                callback(current, max);

                setTimeout(advance, interval);
            }
        }
        
        advance();
    },

    detectIE() {
        let ua = window.navigator.userAgent;
    
        // Test values; Uncomment to check result …
    
        // IE 10
        // ua = 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)';
    
        // IE 11
        // ua = 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko';
    
        // Edge 12 (Spartan)
        // ua = 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/39.0.2171.71 Safari/537.36 Edge/12.0';
    
        // Edge 13
        // ua = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/46.0.2486.0 Safari/537.36 Edge/13.10586';

        let msie = ua.indexOf('MSIE ');
        if (msie > 0) {
            // IE 10 or older => return version number
            return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
        }

        let trident = ua.indexOf('Trident/');
        if (trident > 0) {
            // IE 11 => return version number
            let rv = ua.indexOf('rv:');
            return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
        }

        let edge = ua.indexOf('Edge/');
        if (edge > 0) {
            // Edge (IE 12+) => return version number
            return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
        }
    
        // other browser
        return false;
    },
    
    supportsAutoplay() {
        // var v = document.createElement("video");
        //
        // v.pause();
        // v.play();
        //
        // if (v.paused) {
        //     v.play();
        // }
        //
        // var p = "play" in v && v.play();
        //
        // typeof callback === "function" && callback(!v.paused || "Promise" in window && p instanceof Promise);
        
        // let video = document.createElement("video"),
        //     audio = document.createElement("audio"),
        //     videoPlay,
        //     audioPlay;
        //
        // video.paused = true;
        // videoPlay = "play" in video && video.play();
        //
        // audio.paused = true;
        // audioPlay = "play" in audio && audio.play();
        //
        // return {
        //     video: ! video.paused || "Promise" in window && videoPlay instanceof Promise,
        //     audio: ! audio.paused
        // };
    }
};

export default Helpers;